import * as React from "react";
import { Loader, Modal } from "@grandvisionhq/common";
import { Alert } from "@grandvisionhq/iris-ui";
import { useDispatch, useSelector, useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { useDynamicModule } from "@grandvisionhq/utils";
import { useSetServiceStore } from "../../../../../../hooks/operations/hooks/operations/use-set-service-store.js";
import { getServiceStore } from "../../../../../../selectors.js";
import { resetStore, resetServiceStoreValidation } from "../../../../../../StepShipping/StepShipping/actions.js";
import { getSelectedShippingMethod, shouldValidateServiceStore, getBillingAddress, getShippingDetails } from "../../../../../../StepShipping/StepShipping/selectors.js";
import { useGetPreferredStore } from "../../../../hooks/modules/ServiceStore/hooks/use-get-preferred-store.js";
import { useNearestStore } from "../../../../hooks/modules/ServiceStore/hooks/use-nearest-store.js";
import { ActionButtonContainer } from "./ActionButtonContainer.js";
import { StoreFinderItemContainer } from "./StoreFinderItemContainer.js";
import { StoreSummaryContainer } from "./StoreSummaryContainer.js";
/* empty css                                                               */
import { isPickupStore } from "../../../../../../utils/utils/shipping-method.js";
import { useCheckoutSettings } from "../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const loadModule = async () => {
  const storesModule = await import(
    /* webpackChunkName: 'grandvisionhq-stores-v2' */
    "@grandvisionhq/stores-v2"
  );
  return storesModule;
};
const usePickupStoresModule = () => useDynamicModule(loadModule, "@grandvisionhq/stores-v2", []);
const ServiceStoreContainer = ({ className, handleServiceStoreLoading }) => {
  const [showModal, setShowModal] = React.useState(false);
  const dispatch = useDispatch();
  const { serviceStore: serviceStoreConfig = {} } = useCheckoutSettings();
  const selectedShippingMethod = useSelector(getSelectedShippingMethod);
  const serviceStore = useSelector(getServiceStore);
  const validateServiceStore = useSelector(shouldValidateServiceStore);
  const address = useSelector(getBillingAddress);
  const details = useSelector(getShippingDetails);
  const isPickupStoreSelected = isPickupStore(selectedShippingMethod?.method);
  const serviceStoreIsInValid = validateServiceStore && !serviceStore;
  const serviceStoreIdRef = React.useRef(null);
  const { globalStoreId, isFetchingStoreId } = useGetPreferredStore();
  const nearestStoreQuery = React.useMemo(() => {
    if (details?.billingAddress.isValid) {
      const postalCode = address?.postalCode || "";
      const streetName = address?.streetName || "";
      const streetNumber = address?.streetNumber || "";
      const city = address?.city || "";
      return `${postalCode} ${streetName} ${streetNumber} ${city}`.replace(/\s{2,}/g, " ").trim();
    }
    return "";
  }, [address, details]);
  const [storeId, isLoading] = useNearestStore(
    nearestStoreQuery,
    !!serviceStoreConfig.preselectFromShippingData && !serviceStore
  );
  const [serviceStoreIdState, setServiceStoreIdState] = React.useState(null);
  const { setServiceStore, isUpdating } = useSetServiceStore({
    onError: () => {
      setServiceStoreIdState(null);
      dispatch(resetStore());
    }
  });
  React.useEffect(() => {
    handleServiceStoreLoading?.({ isServiceStoreLoading: isUpdating });
  }, [isUpdating]);
  React.useEffect(() => {
    if (storeId && !serviceStoreIdState) {
      setServiceStoreIdState(storeId);
    }
  }, [serviceStoreIdState, storeId, isPickupStoreSelected]);
  const [module, loading, error] = usePickupStoresModule();
  const { features } = useCheckoutSettings();
  const featuresToggles = useFeatureToggles();
  const { getLabel } = useIntl();
  const labels = React.useMemo(
    () => ({
      title: getLabel("checkout.serviceStore.title"),
      subtitle: getLabel("checkout.serviceStore.subtitle"),
      errorMessage: getLabel("checkout.serviceStore.error")
    }),
    [getLabel]
  );
  React.useEffect(() => {
    if (serviceStoreConfig.isOptional) {
      dispatch(resetServiceStoreValidation());
    }
  }, [dispatch, serviceStoreConfig.isOptional]);
  const onStoreSelect = React.useCallback(
    (store) => {
      setShowModal(false);
      if (store?.globalStoreId) {
        setServiceStoreIdState(store.globalStoreId);
        setServiceStore(store.globalStoreId);
      }
    },
    [setServiceStore]
  );
  const onEdit = React.useCallback(() => {
    setShowModal(true);
  }, []);
  const onCloseModal = React.useCallback(() => {
    setShowModal(false);
  }, []);
  const resetServiceStore = React.useCallback(() => {
    setServiceStoreIdState(null);
  }, []);
  const handleStoreChange = React.useCallback(
    (storeId2) => {
      if (storeId2 !== serviceStoreIdState) {
        setServiceStoreIdState(storeId2);
        setServiceStore(storeId2);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceStoreIdState]
  );
  React.useEffect(() => {
    if (!serviceStore?.globalStoreId && globalStoreId && globalStoreId !== serviceStoreIdRef.current) {
      handleStoreChange(globalStoreId);
      serviceStoreIdRef.current = globalStoreId;
    } else if (serviceStore?.globalStoreId && serviceStore?.globalStoreId !== serviceStoreIdRef.current) {
      handleStoreChange(serviceStore?.globalStoreId);
      serviceStoreIdRef.current = serviceStore?.globalStoreId;
    }
  }, [globalStoreId, serviceStore?.globalStoreId]);
  if (!features?.isServiceStoreEnabled || isPickupStoreSelected) {
    return null;
  }
  if (loading || isFetchingStoreId) {
    return /* @__PURE__ */ React.createElement(Loader, null);
  }
  if (error || !module) {
    return /* @__PURE__ */ React.createElement(Alert, { variant: "error", notifyScreenReader: false, className: "alert-m-default" }, labels.errorMessage);
  }
  const { Components, Containers, StoreFinderProvider } = module;
  const { StoreFinder, PickUpButtons, withPickUpButtonsConfig } = Containers;
  const { StoreListTopContent } = Components;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, serviceStoreIsInValid && /* @__PURE__ */ React.createElement(Alert, { variant: "error", notifyScreenReader: false, className: "alert-m-default" }, labels.errorMessage), serviceStoreIdState ? /* @__PURE__ */ React.createElement(
    StoreSummaryContainer,
    {
      className,
      globalStoreId: serviceStoreIdState,
      actions: { onEdit },
      resetServiceStore
    }
  ) : /* @__PURE__ */ React.createElement(
    ActionButtonContainer,
    {
      onClick: onEdit,
      isLoading,
      isValid: !serviceStoreIsInValid
    }
  ), /* @__PURE__ */ React.createElement(
    Modal,
    {
      className: "service-store__modal",
      showIcon: true,
      iconClassName: "service-store__modal__close",
      isOpen: showModal,
      onModalClose: onCloseModal
    },
    /* @__PURE__ */ React.createElement(
      StoreFinderProvider,
      {
        googleMapsId: "google-maps-service-store",
        hidePhone: !featuresToggles.isStorePickupPhoneEnabled,
        hideEmail: !featuresToggles.isStorePickupEmailEnabled
      },
      /* @__PURE__ */ React.createElement(
        StoreFinder,
        {
          isInsideModal: true,
          listTopContent: /* @__PURE__ */ React.createElement(StoreListTopContent, { title: labels.title, description: labels.subtitle }),
          listItemComponent: (props) => /* @__PURE__ */ React.createElement(
            StoreFinderItemContainer,
            {
              ...props,
              variant: "list-item",
              onClick: onStoreSelect
            }
          ),
          infoWindowContentComponent: (props) => /* @__PURE__ */ React.createElement(StoreFinderItemContainer, { ...props, variant: "map-item", onClick: onStoreSelect }),
          buttonsComponent: withPickUpButtonsConfig(PickUpButtons, { onStoreSelect })
        }
      )
    )
  ));
};
export {
  ServiceStoreContainer
};
//# sourceMappingURL=ServiceStoreContainer.js.map
